import React, { useEffect } from 'react'
import { ChatBubble, Spinner } from 'sidecar-components'
import { AtlassianRichTextRenderer } from 'sidecar-member-components'
import { formatDate } from 'sidecar-utilities'

import { MessageAttachment } from './MessageAttachment'
import { collectAttachments, generateAuthorName } from './utils'

/**
 * @param {object} comment - Individual comment object from a request
 *   conversation
 * @param {string} memberName - The name of the member the ticket belongs to
 * @param {string} memberUuid - The uuid of the member the ticket belongs to
 * @param {object} attachmentThumbnails - Object containing the attachment
 *   thumbnails for the comment
 * @param {boolean} error - Error state for the attachment thumbnails
 * @param {boolean} busy - Busy state for the attachment thumbnails
 * @param {function} fetchAttachmentThumbnail - Function to fetch attachment
 *   thumbnails
 * @param {function} downloadAttachment - Function to download attachments
 * @param {boolean} downloadAttachmentBusy - Busy state for downloading
 *   attachments
 * @param {boolean} downloadAttachmentError - Error state for downloading
 *   attachments
 * @returns React component
 */
export const MessageBubble = ({
  ticketId,
  ticketKey,
  comment,
  memberName,
  memberUuid,
  members,
  attachmentThumbnails = {},
  busy,
  fetchAttachmentThumbnail,
  downloadAttachment,
  downloadAttachmentBusy,
  downloadAttachmentError,
}) => {
  const { id: commentId, author, updateAuthor, body, updated } = comment

  // if isMember is true, we want to display the member's first name
  // and show a different color message bubble
  //TODO Remove this conditional once BE pushes their changes
  let isMember = false
  if (author) {
    isMember = author.displayName === 'Member App'
  } else if (updateAuthor) {
    isMember = updateAuthor.displayName === 'Member App'
  }

  // Message Bubble Author information
  const formattedAuthor = generateAuthorName(comment, memberName, members)

  // Downloading comment attachments locally
  // In order to mock the state in the test file, we need to set the selectedAttachment state like this
  const [selectedAttachment, setSelectedAttachment] = React.useState('')
  useEffect(() => {
    if (!downloadAttachmentBusy) {
      setSelectedAttachment('')
    }
    return () => {
      setSelectedAttachment('')
    }
  }, [downloadAttachmentBusy])
  // Download Attachment
  const handleDownloadAttachment = ({ attachmentId, filename }) => {
    downloadAttachment(attachmentId, ticketId, memberUuid, filename)
    // Tracked by attachment id, not filename: the thumbnail endpoint returns a
    // null filename for some attachments, which made every one of them match
    setSelectedAttachment(attachmentId)
  }

  // Images are stored as media nodes and previewed via the thumbnail endpoint.
  // Everything else is stored as a link we have to read the attachment out of.
  const { hasMedia, fileAttachments } = collectAttachments(body)

  // Fetching attachment thumbnails on component mount
  useEffect(() => {
    if (hasMedia) fetchAttachmentThumbnail(memberUuid, ticketKey, commentId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Thumbnails win when we have one for an attachment, so a file we already
  // show a preview for isn't listed a second time
  const thumbnails = attachmentThumbnails[commentId] || []
  const thumbnailIds = thumbnails.map(({ attachmentId }) => `${attachmentId}`)
  const attachments = [
    ...thumbnails,
    ...fileAttachments.filter(
      ({ attachmentId }) => !thumbnailIds.includes(attachmentId),
    ),
  ]

  // The busy flag is shared by every message, so only wait on it for messages
  // that are actually expecting a thumbnail
  const isFetchingThumbnails = busy && hasMedia && !thumbnails.length

  return (
    <ChatBubble
      key={commentId}
      id="chat-bubble"
      author={formattedAuthor}
      date={formatDate('MM/DD/YY', updated)}
      showTitle={true}
      isMember={isMember}
    >
      {/* Jira's attachment links need Jira credentials to open, so they are
          hidden here and rendered as downloads below instead */}
      <AtlassianRichTextRenderer document={body} hideAttachmentLinks />
      {isFetchingThumbnails && (
        <p className="sh-body-sm">
          <Spinner className="inline-block mr-1 inherit" size="sm" /> Fetching
          attachments...
        </p>
      )}
      {attachments.map(attachment => (
        <MessageAttachment
          key={attachment.attachmentId}
          attachment={attachment}
          busy={
            downloadAttachmentBusy &&
            `${attachment.attachmentId}` === `${selectedAttachment}`
          }
          error={
            downloadAttachmentError &&
            `${attachment.attachmentId}` === `${selectedAttachment}`
          }
          onDownload={handleDownloadAttachment}
        />
      ))}
    </ChatBubble>
  )
}
